import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@cloudscape-design/components/box';

/**
 * Props for the SessionTimer component.
 */
export interface SessionTimerProps {
  /** Duration of the timer in minutes */
  durationMinutes: number;
  /** Callback function invoked exactly once when the timer reaches zero */
  onTimeUp: () => void;
}

/**
 * Formats seconds into MM:SS format.
 * @param totalSeconds - The total number of seconds to format
 * @returns A string in MM:SS format
 */
export function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * SessionTimer Component
 * 
 * A countdown timer component for voice training sessions.
 * 
 * Features:
 * - Starts countdown from durationMinutes * 60 seconds
 * - Updates every second
 * - Displays time in MM:SS format
 * - Calls onTimeUp callback exactly once when timer reaches zero
 * - Cleans up interval on unmount
 * 
 * @validates Requirements 8.1 - Displays a 10-minute countdown timer
 * @validates Requirements 8.2 - Automatically ends the session when timer reaches zero
 */
export function SessionTimer({ durationMinutes, onTimeUp }: SessionTimerProps): React.ReactElement {
  // Initialize with total seconds from duration
  const [timeRemaining, setTimeRemaining] = useState<number>(
    Math.max(0, Math.floor(durationMinutes * 60))
  );
  
  // Track if onTimeUp has been called to ensure it's called exactly once
  const hasCalledOnTimeUp = useRef<boolean>(false);
  
  // Store the callback in a ref to avoid stale closure issues
  const onTimeUpRef = useRef(onTimeUp);
  
  // Update the ref when onTimeUp changes
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);

  // Memoized callback to handle timer reaching zero
  const handleTimeUp = useCallback(() => {
    if (!hasCalledOnTimeUp.current) {
      hasCalledOnTimeUp.current = true;
      onTimeUpRef.current();
    }
  }, []);

  useEffect(() => {
    // If duration is 0 or negative, call onTimeUp immediately
    if (durationMinutes <= 0) {
      handleTimeUp();
      return;
    }

    // Set up the interval to decrement every second
    const intervalId = setInterval(() => {
      setTimeRemaining((prevTime) => {
        if (prevTime <= 1) {
          // Clear interval and call callback when reaching zero
          clearInterval(intervalId);
          handleTimeUp();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    // Cleanup function to clear interval on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [durationMinutes, handleTimeUp]);

  // Determine color based on time remaining
  const getTimeColor = (): 'text-status-success' | 'text-status-warning' | 'text-status-error' => {
    if (timeRemaining > 120) {
      return 'text-status-success'; // Green when more than 2 minutes
    } else if (timeRemaining > 60) {
      return 'text-status-warning'; // Yellow when 1-2 minutes
    }
    return 'text-status-error'; // Red when less than 1 minute
  };

  return (
    <Box
      data-testid="session-timer"
      textAlign="center"
      padding="s"
    >
      <Box
        variant="h2"
        color={getTimeColor()}
        data-testid="timer-display"
      >
        {formatTime(timeRemaining)}
      </Box>
      <Box
        variant="small"
        color="text-body-secondary"
      >
        Time Remaining
      </Box>
    </Box>
  );
}

export default SessionTimer;
